import { z } from 'zod';
import { DISCHARGE_NO_PLAN_REASONS_DEFAULT, NO_PLAN_REASONS_MAX, NO_PLAN_REASON_MAX, noPlanReasonsOf, } from './dischargeNote.js';
/**
 * ehr#682 (PROGRESS-NOTE-10-20-V141, Note settings > Practice note settings; V126, V133 to V136):
 * the practice's progress note rules, set by supervisors and admins. Stored in
 * organizations."clinicalSettings" next to lateEntryHours (GET /practice/clinical reads them, any
 * user; PUT /practice/note-settings writes them, admin, manager or supervisor).
 *
 * Keys other agents read (keep them stable):
 *   aiForAssociates    Generate notes with AI for associates (V126: associates stay Off until their
 *                      supervisor turns it on). An associate sees "Set by your supervisor".
 *   allowSkipRequired  Allow skipping required boxes at sign-off.
 *   requireAddressedFocus  At least one problem, goal, or objective must be addressed (V133,
 *                      default on). Same key the addressed-focus sign-off gate reads (ehr#682
 *                      PR #753, `requireAddressedFocusOf`); `requireFocusOn()` here reads it too.
 *   keepRevertByRole   Must Keep or Revert each supervisor edit before submitting, per role (V136).
 *   requireGoalStatus  ehr#708 (Jay 10-30, Q3): Goal status required at sign-off (default on): a
 *                      discharge summary needs a final status on every goal and objective.
 *   dischargeNoPlanReasons  ehr#708 (Q2): the "No treatment plan on file" reasons a discharge
 *                      summary offers (the practice adds to the four it starts with).
 *   caseManagementGoals  ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V9, 20 A to 22 A): "Show treatment plan
 *                      goals" on case management notes, default off. On, a case management note shows
 *                      Treatment plan goals and Problems, goals, and objectives addressed, and the
 *                      addressed-focus sign-off gate applies to it (`noteBlocks`, `focusLeft`).
 *   noShowSupervisorSignoff  ehr#731 (Jay 10-30 on #713): an associate's no-show / cancellation
 *                      note goes to their supervisor (default Off; a ticked Risk concern or
 *                      Leading to discharge sends it anyway, `supervisorReviews` in noShowNote.ts).
 *
 * The EHR has no "Associate" role. V141's associate is a provider whose notes need a supervisor's
 * co-signature: the Supervision setting on their Provider Profile (`supervisionFrom().required`,
 * GET /progress-notes/supervision). `noteRoleOf()` makes the mapping.
 */
export const NOTE_ROLES = ['associate', 'clinician', 'supervisor'];
/** V141 ROLE_NAMES, plural, as the Keep or Revert checkboxes read. */
export const NOTE_ROLE_PLURAL = {
    associate: 'Associates',
    clinician: 'Clinicians',
    supervisor: 'Supervisors',
};
export const PRACTICE_NOTE_SETTING_KEYS = {
    aiForAssociates: 'aiForAssociates',
    allowSkipRequired: 'allowSkipRequired',
    requireAddressedFocus: 'requireAddressedFocus',
    keepRevertByRole: 'keepRevertByRole',
    requireGoalStatus: 'requireGoalStatus',
    dischargeNoPlanReasons: 'dischargeNoPlanReasons',
    caseManagementGoals: 'caseManagementGoals',
    noShowSupervisorSignoff: 'noShowSupervisorSignoff',
};
const keepRevertByRoleSchema = z.object({
    associate: z.boolean().default(true),
    clinician: z.boolean().default(true),
    supervisor: z.boolean().default(false),
});
/**
 * V141 defaults: AI off for associates, no skipping, a focus required, Keep or Revert for associates
 * and clinicians. ehr#725 (V9 20 A): case management notes show no plan goals until turned on.
 */
export const PRACTICE_NOTE_DEFAULTS = {
    aiForAssociates: false,
    allowSkipRequired: false,
    requireAddressedFocus: true,
    keepRevertByRole: { associate: true, clinician: true, supervisor: false },
    requireGoalStatus: true,
    dischargeNoPlanReasons: DISCHARGE_NO_PLAN_REASONS_DEFAULT,
    caseManagementGoals: false,
    noShowSupervisorSignoff: false,
};
/** ehr#708: the reason list as it is sent and stored (each one line, at most 80 long). */
const noPlanReasonsSchema = z
    .array(z.string().trim().min(1).max(NO_PLAN_REASON_MAX))
    .min(1, 'Keep at least one reason.')
    .max(NO_PLAN_REASONS_MAX);
/** The stored shape, with V141's defaults for anything not set yet. */
export const practiceNoteSettingsSchema = z.object({
    aiForAssociates: z.boolean().default(PRACTICE_NOTE_DEFAULTS.aiForAssociates),
    allowSkipRequired: z.boolean().default(PRACTICE_NOTE_DEFAULTS.allowSkipRequired),
    requireAddressedFocus: z.boolean().default(PRACTICE_NOTE_DEFAULTS.requireAddressedFocus),
    keepRevertByRole: keepRevertByRoleSchema.default({ ...PRACTICE_NOTE_DEFAULTS.keepRevertByRole }),
    requireGoalStatus: z.boolean().default(PRACTICE_NOTE_DEFAULTS.requireGoalStatus),
    dischargeNoPlanReasons: z
        .array(z.string())
        .default([...DISCHARGE_NO_PLAN_REASONS_DEFAULT])
        .transform((v) => noPlanReasonsOf(v)),
    caseManagementGoals: z.boolean().default(PRACTICE_NOTE_DEFAULTS.caseManagementGoals),
    noShowSupervisorSignoff: z.boolean().default(PRACTICE_NOTE_DEFAULTS.noShowSupervisorSignoff),
});
/** PUT /practice/note-settings: only the settings sent change; Keep or Revert is sent whole. */
export const practiceNoteSettingsInputSchema = z
    .object({
    aiForAssociates: z.boolean().optional(),
    allowSkipRequired: z.boolean().optional(),
    requireAddressedFocus: z.boolean().optional(),
    keepRevertByRole: z
        .object({ associate: z.boolean(), clinician: z.boolean(), supervisor: z.boolean() })
        .strict()
        .optional(),
    requireGoalStatus: z.boolean().optional(),
    dischargeNoPlanReasons: noPlanReasonsSchema.optional(),
    caseManagementGoals: z.boolean().optional(),
    noShowSupervisorSignoff: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Send a setting to change.',
});
/** Who sets them (V135: supervisors as well as practice admins; managers are admins here). */
export const PRACTICE_NOTE_SETTINGS_ROLES = ['admin', 'manager', 'supervisor'];
export const canEditPracticeNoteSettings = (role) => PRACTICE_NOTE_SETTINGS_ROLES.includes(role ?? '');
/**
 * Read them from the stored clinical settings (or GET /practice/clinical), field by field: a value
 * that doesn't fit reads as its default instead of losing the others.
 */
export function readPracticeNoteSettings(v) {
    const o = v && typeof v === 'object' && !Array.isArray(v) ? v : {};
    const bool = (x, d) => (typeof x === 'boolean' ? x : d);
    const k = o.keepRevertByRole && typeof o.keepRevertByRole === 'object'
        ? o.keepRevertByRole
        : {};
    const d = PRACTICE_NOTE_DEFAULTS;
    return {
        aiForAssociates: bool(o.aiForAssociates, d.aiForAssociates),
        allowSkipRequired: bool(o.allowSkipRequired, d.allowSkipRequired),
        requireAddressedFocus: bool(o.requireAddressedFocus, d.requireAddressedFocus),
        keepRevertByRole: {
            associate: bool(k.associate, d.keepRevertByRole.associate),
            clinician: bool(k.clinician, d.keepRevertByRole.clinician),
            supervisor: bool(k.supervisor, d.keepRevertByRole.supervisor),
        },
        requireGoalStatus: bool(o.requireGoalStatus, d.requireGoalStatus),
        dischargeNoPlanReasons: noPlanReasonsOf(o.dischargeNoPlanReasons),
        caseManagementGoals: bool(o.caseManagementGoals, d.caseManagementGoals),
        noShowSupervisorSignoff: bool(o.noShowSupervisorSignoff, d.noShowSupervisorSignoff),
    };
}
/**
 * The person's role for these rules: an associate when their notes need a co-signature (checked
 * first, as V141's roleOf checks the associate license first), a supervisor by EHR role, else a
 * clinician (admins, managers and front desk who write notes count as clinicians).
 */
export function noteRoleOf(role, supervised) {
    if (supervised)
        return 'associate';
    return role === 'supervisor' ? 'supervisor' : 'clinician';
}
/** V136: this role must Keep or Revert each supervisor edit before submitting. */
export const keepRevertRequired = (s, role) => s.keepRevertByRole[role];
/** V133: the sign-off gate "At least one problem, goal, or objective addressed" is on. */
export const requireFocusOn = (s) => s.requireAddressedFocus;
/**
 * V126 / V141 aiMode: an associate's AI is set by their supervisor (the practice's
 * aiForAssociates; their own choice is not used); everyone else's is their own.
 */
export const aiSetBySupervisor = (role) => role === 'associate';
export function noteAiFor(own, role, s) {
    if (!aiSetBySupervisor(role))
        return own;
    return s.aiForAssociates ? 'open' : 'off';
}
//# sourceMappingURL=practiceNoteSettings.js.map